import { useEffect, useState, useMemo } from 'react';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';
import PiscinaBuscador from '../components/PiscinaBuscador';
import { format } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, LabelList,
} from 'recharts';

const KG_POR_SACO = 25;

/* ────────────────────────────────────────────
   Helpers ISO 8601
   ──────────────────────────────────────────── */
function isoDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getLunesYDomingoDeSemana(year, semana) {
  const ene4 = new Date(Date.UTC(year, 0, 4));
  const dow = ene4.getUTCDay() || 7;
  const lunesSem1 = new Date(ene4);
  lunesSem1.setUTCDate(ene4.getUTCDate() - dow + 1);

  const lunes = new Date(lunesSem1);
  lunes.setUTCDate(lunesSem1.getUTCDate() + (semana - 1) * 7);

  const domingo = new Date(lunes);
  domingo.setUTCDate(lunes.getUTCDate() + 6);

  return { lunes: isoDate(lunes), domingo: isoDate(domingo) };
}

function getISOWeekKey(fecha) {
  const [anio, mes, dia] = fecha.split('-').map(Number);
  const d = new Date(Date.UTC(anio, mes - 1, dia));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;
}

const fmtCorto = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

function colorPorcentaje(pct) {
  if (pct == null || isNaN(pct)) return '#9CA3AF';
  if (pct <= 75 || pct > 125) return '#DC2626';
  if (pct > 75 && pct <= 85) return '#F59E0B';
  if (pct > 115 && pct <= 125) return '#F59E0B';
  if (pct > 85 && pct <= 115) return '#16A34A';
  return '#9CA3AF';
}

function textoSemaforo(pct) {
  if (pct == null || isNaN(pct)) return '-';
  if (pct <= 75 || pct > 125) return '🔴 Crítico';
  if (pct > 75 && pct <= 85) return '🟡 Bajo';
  if (pct > 115 && pct <= 125) return '🟡 Alto';
  if (pct > 85 && pct <= 115) return '🟢 Óptimo';
  return '-';
}

function formatearFechaCorta(fecha) {
  if (!fecha) return '';
  const [anio, mes, dia] = fecha.split('-');
  return `${parseInt(dia)}/${parseInt(mes)}/${anio}`;
}

const textoModo = {
  cronograma: 'Cronograma',
  sonico: 'Automático',
  hibrido: 'Híbrido',
  automatico: 'Automático',
};

const colorModo = {
  cronograma: '#0D47A1',
  sonico: '#6A1B9A',
  hibrido: '#EA580C',
  automatico: '#6A1B9A',
};

const clavePiscina = (sector, codigo) =>
  `${(sector || 'sin-sector').trim().toLowerCase()}||${(codigo || 'sin-codigo').trim().toLowerCase()}`;

export default function ReporteRegistroDiario() {
  const fincaIdContext = useFincaId();
  const hoy = new Date().toISOString().split('T')[0];
  const hace30 = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  const [sectores, setSectores] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [corridas, setCorridas] = useState([]);
  const [todasPiscinas, setTodasPiscinas] = useState([]);

  const [modoFecha, setModoFecha] = useState('rango');
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(true);

  const [fechaDesde, setFechaDesde] = useState(hace30);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [sectorId, setSectorId] = useState('');
  const [piscinaId, setPiscinaId] = useState('');
  const [siembraId, setSiembraId] = useState('');
  const [tipoBalanceadoFiltro, setTipoBalanceadoFiltro] = useState('');

  const [semanaDesdeKey, setSemanaDesdeKey] = useState('');
  const [semanaHastaKey, setSemanaHastaKey] = useState('');
  const [semanasDisponibles, setSemanasDisponibles] = useState([]);
  const [cargandoSemanas, setCargandoSemanas] = useState(false);

  const [piscinasCosechadasInfo, setPiscinasCosechadasInfo] = useState([]);
  const [cargandoCosechadas, setCargandoCosechadas] = useState(false);

  const piscinasCosechadasIds = useMemo(
    () => new Set(piscinasCosechadasInfo.map((p) => p.piscina_id)),
    [piscinasCosechadasInfo]
  );
  const sectoresConCosechadasIds = useMemo(
    () => new Set(piscinasCosechadasInfo.map((p) => p.sector_id)),
    [piscinasCosechadasInfo]
  );

  const piscinasActivasIds = useMemo(
    () => new Set(todasPiscinas.filter((p) => p.activo !== false).map((p) => p.id)),
    [todasPiscinas]
  );
  const sectoresConActivasIds = useMemo(
    () => new Set(
      todasPiscinas.filter((p) => p.activo !== false).map((p) => p.sector_id)
    ),
    [todasPiscinas]
  );

  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [mostrarResumen, setMostrarResumen] = useState(true);

  useEffect(() => {
    cargarSectores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  useEffect(() => {
    if (sectorId) cargarPiscinas(sectorId);
    else setPiscinas([]);
    setPiscinaId('');
    setSiembraId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId]);

  useEffect(() => {
    if (piscinaId) cargarCorridas(piscinaId);
    else setCorridas([]);
    setSiembraId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piscinaId]);

  useEffect(() => {
    setTipoBalanceadoFiltro('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta, sectorId, piscinaId, siembraId]);

  useEffect(() => {
    if (fechaDesde && fechaHasta) cargarReporte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta, sectorId, piscinaId, siembraId, modoFecha]);

  useEffect(() => {
    if (modoFecha !== 'historial') {
      setPiscinasCosechadasInfo([]);
      return;
    }
    let activo = true;
    setCargandoCosechadas(true);
    api.get('/registro-diario/piscinas-cosechadas')
      .then(({ data }) => {
        if (activo) setPiscinasCosechadasInfo(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (activo) setPiscinasCosechadasInfo([]);
      })
      .finally(() => {
        if (activo) setCargandoCosechadas(false);
      });
    return () => { activo = false; };
  }, [modoFecha, fincaIdContext]);

  useEffect(() => {
    if (modoFecha !== 'semana' && modoFecha !== 'rangoSemanas' && modoFecha !== 'historial') return;

    let activo = true;
    setCargandoSemanas(true);

    const params = {};
    if (sectorId)  params.sector_id  = sectorId;
    if (piscinaId) params.piscina_id = piscinaId;
    if (modoFecha === 'historial') params.solo_cosechadas = 'true';

    api.get('/registro-diario/semanas-con-datos', { params })
      .then(({ data }) => {
        if (!activo) return;
        const conRango = (data || []).map((s) => {
          const { lunes, domingo } = getLunesYDomingoDeSemana(s.year, s.semana);
          return { ...s, lunes, domingo, key: `${s.year}-${String(s.semana).padStart(2, '0')}` };
        });
        setSemanasDisponibles(conRango);
        if (conRango.length > 0) {
          const primera = conRango[0];
          if (!semanaDesdeKey) {
            setSemanaDesdeKey(primera.key);
            setFechaDesde(primera.lunes);
          }
          if (!semanaHastaKey) {
            setSemanaHastaKey(primera.key);
            setFechaHasta(primera.domingo);
          }
        }
      })
      .catch(() => {
        if (activo) setSemanasDisponibles([]);
      })
      .finally(() => {
        if (activo) setCargandoSemanas(false);
      });

    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoFecha, sectorId, piscinaId]);

  const cambiarSemanaUnica = (key) => {
    setSemanaDesdeKey(key);
    setSemanaHastaKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (sem) {
      setFechaDesde(sem.lunes);
      setFechaHasta(sem.domingo);
    }
  };

  const cambiarSemanaDesde = (key) => {
    setSemanaDesdeKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (!sem) return;
    setFechaDesde(sem.lunes);
    const semHasta = semanasDisponibles.find((s) => s.key === semanaHastaKey);
    if (!semHasta || sem.lunes > semHasta.lunes) {
      setSemanaHastaKey(key);
      setFechaHasta(sem.domingo);
    }
  };

  const cambiarSemanaHasta = (key) => {
    setSemanaHastaKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (!sem) return;
    setFechaHasta(sem.domingo);
    const semDesde = semanasDisponibles.find((s) => s.key === semanaDesdeKey);
    if (!semDesde || sem.domingo < semDesde.domingo) {
      setSemanaDesdeKey(key);
      setFechaDesde(sem.lunes);
    }
  };

  const cambiarModo = (nuevo) => {
    setModoFecha(nuevo);
    if (nuevo === 'semana' || nuevo === 'rangoSemanas' || nuevo === 'historial') {
      setSemanaDesdeKey('');
      setSemanaHastaKey('');
    }
    if (nuevo !== 'historial') {
      setPiscinaId('');
      setSiembraId('');
    }
  };

  const piscinasFiltradasParaModo = (lista) => {
    if (modoFecha === 'historial') {
      if (piscinasCosechadasIds.size === 0) return [];
      return lista.filter((p) => piscinasCosechadasIds.has(p.id));
    }
    return lista.filter((p) => p.activo !== false);
  };

  const sectoresVisibles = useMemo(() => {
    if (modoFecha === 'historial') {
      return sectores.filter((s) => sectoresConCosechadasIds.has(s.id));
    }
    return sectores.filter((s) => sectoresConActivasIds.has(s.id));
  }, [sectores, modoFecha, sectoresConCosechadasIds, sectoresConActivasIds]);

  useEffect(() => {
    if (!sectorId) return;
    if (!sectoresVisibles.some((s) => s.id === sectorId)) {
      setSectorId('');
    }
  }, [sectoresVisibles, sectorId]);

  const cargarSectores = async () => {
    try {
      if (!fincaIdContext) {
        setSectores([]);
        setTodasPiscinas([]);
        return;
      }
      const { data: secs } = await api.get(`/sectores/finca/${fincaIdContext}`);
      setSectores(secs);
      cargarTodasPiscinas(secs);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarTodasPiscinas = async (sectoresList) => {
    if (!sectoresList || sectoresList.length === 0) {
      setTodasPiscinas([]);
      return;
    }
    try {
      const promesas = sectoresList.map((s) =>
        api.get(`/piscinas/sector/${s.id}?incluirInactivas=true`)
          .then((res) => (res.data || []).map((p) => ({
            ...p,
            sector_id: p.sector_id || s.id,
            sector_nombre: s.nombre,
          })))
          .catch(() => [])
      );
      const resultados = await Promise.all(promesas);
      setTodasPiscinas(resultados.flat());
    } catch (err) {
      console.error('Error cargando todas las piscinas:', err);
      setTodasPiscinas([]);
    }
  };

  const cargarPiscinas = async (id) => {
    try {
      const { data } = await api.get(`/piscinas/sector/${id}?incluirInactivas=true`);
      setPiscinas(data);
    } catch (err) { console.error(err); }
  };

  const cargarCorridas = async (id) => {
    try {
      const { data } = await api.get(`/siembras/piscina/${id}`);
      setCorridas(data);
    } catch (err) { setCorridas([]); }
  };

  const cargarReporte = async () => {
    setCargando(true);
    try {
      const params = { desde: fechaDesde, hasta: fechaHasta };
      if (sectorId) params.sector_id = sectorId;
      if (piscinaId) params.piscina_id = piscinaId;
      if (siembraId) params.siembra_id = siembraId;
      if (modoFecha === 'historial') params.solo_cosechadas = 'true';

      const { data } = await api.get('/registro-diario/reporte', { params });
      setDatos(data);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  };

  const exportarExcel = async () => {
    if (datos.length === 0) return;
    try {
      const params = { desde: fechaDesde, hasta: fechaHasta };
      if (sectorId) params.sector_id = sectorId;
      if (piscinaId) params.piscina_id = piscinaId;
      if (siembraId) params.siembra_id = siembraId;
      if (tipoBalanceadoFiltro) params.tipo_balanceado = tipoBalanceadoFiltro;
      if (modoFecha === 'historial') params.solo_cosechadas = 'true';

      const res = await api.get('/registro-diario/reporte/exportar/excel', {
        params,
        responseType: 'blob',
      });

      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;

      let sufijo;
      if (modoFecha === 'rango') {
        sufijo = `${fechaDesde}_a_${fechaHasta}`;
      } else if (modoFecha === 'semana') {
        sufijo = `semana_${semanaDesdeKey.replace('-', '_')}`;
      } else {
        const prefijo = modoFecha === 'historial' ? 'historial_' : 'semanas_';
        sufijo = semanaDesdeKey === semanaHastaKey
          ? `semana_${semanaDesdeKey.replace('-', '_')}`
          : `${prefijo}${semanaDesdeKey.replace('-', '_')}_a_${semanaHastaKey.replace('-', '_')}`;
      }
      a.download = `registro_diario_${sufijo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando:', err);
      alert('Error al exportar. Revisa la consola.');
    }
  };

  const datosBase = useMemo(() => {
    if (modoFecha === 'historial') return datos;
    return datos.filter((r) => piscinasActivasIds.has(r.piscina_id));
  }, [datos, modoFecha, piscinasActivasIds]);

  const tiposBalanceadoUsados = (() => {
    const set = new Set();
    datosBase.forEach((r) => {
      if (r.tipo_balanceado && r.tipo_balanceado.trim() !== '') {
        set.add(r.tipo_balanceado.trim());
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  })();

  const datosFiltrados = tipoBalanceadoFiltro
    ? datosBase.filter((r) => (r.tipo_balanceado || '').trim() === tipoBalanceadoFiltro)
    : datosBase;

  const agruparPorSemana = modoFecha === 'rangoSemanas' || modoFecha === 'historial';

  const grupos = {};
  datosFiltrados.forEach((r) => {
    const key = agruparPorSemana ? getISOWeekKey(r.fecha) : r.fecha;
    if (!grupos[key]) {
      grupos[key] = { key, fechas: [], proyectado: 0, dosificado: 0 };
    }
    grupos[key].fechas.push(r.fecha);
    grupos[key].proyectado += Number(r.dosis_proyectada_kg || 0);
    grupos[key].dosificado += Number(r.dosificado_pondlogs || 0);
  });

  const datosGrafico = Object.values(grupos)
    .sort((a, b) => {
      const fa = [...a.fechas].sort()[0];
      const fb = [...b.fechas].sort()[0];
      return fa.localeCompare(fb);
    })
    .map((d) => {
      const fechasOrdenadas = [...d.fechas].sort();
      const primera = fechasOrdenadas[0];
      const ultima = fechasOrdenadas[fechasOrdenadas.length - 1];

      let fechaLabel;
      let fechaCompleta;

      if (agruparPorSemana) {
        const [, weekStr] = d.key.split('-');
        const semanaNum = String(weekStr).padStart(2, '0');
        fechaLabel = `S${semanaNum}`;
        fechaCompleta = `Semana ${semanaNum} · ${formatearFechaCorta(primera)} → ${formatearFechaCorta(ultima)}`;
      } else {
        fechaLabel = formatearFechaCorta(primera);
        fechaCompleta = formatearFechaCorta(primera);
      }

      return {
        key: d.key,
        fechas: fechasOrdenadas,
        fechaLabel,
        fechaCompleta,
        proyectado: Math.round(d.proyectado * 100) / 100,
        dosificado: Math.round(d.dosificado * 100) / 100,
        porcentaje: d.proyectado > 0 ? (d.dosificado / d.proyectado) * 100 : null,
      };
    });

  const totalProyectado = datosGrafico.reduce((a, d) => a + d.proyectado, 0);
  const totalDosificado = datosGrafico.reduce((a, d) => a + d.dosificado, 0);
  const pctTotal = totalProyectado > 0 ? (totalDosificado / totalProyectado) * 100 : null;
  const totalProyectadoSacos = totalProyectado / KG_POR_SACO;
  const totalDosificadoSacos = totalDosificado / KG_POR_SACO;
  
  const sumaPorPiscina = {};
  
  datosFiltrados.forEach((r) => {
    const k = clavePiscina(r.sector_nombre, r.piscina_codigo);
    if (!sumaPorPiscina[k]) {
      sumaPorPiscina[k] = { proyectado: 0, dosificado: 0, dias: new Set(), fechas: [] };
    }
    sumaPorPiscina[k].proyectado += Number(r.dosis_proyectada_kg || 0);
    sumaPorPiscina[k].dosificado += Number(r.dosificado_pondlogs || 0);
    if (r.fecha) {
      sumaPorPiscina[k].dias.add(r.fecha);
      sumaPorPiscina[k].fechas.push(r.fecha);
    }
  });

  let piscinasBase = piscinasFiltradasParaModo(todasPiscinas);
  if (piscinaId) {
    piscinasBase = piscinasBase.filter((p) => p.id === piscinaId);
  } else if (sectorId) {
    piscinasBase = piscinasBase.filter((p) => p.sector_id === sectorId);
  }

  const resumenPiscinas = piscinasBase
    .map((p) => {
      const k = clavePiscina(p.sector_nombre, p.codigo);
      const s = sumaPorPiscina[k] || { proyectado: 0, dosificado: 0, dias: new Set(), fechas: [] };
      const areaHa = p.area_ha != null ? Number(p.area_ha) : 0;
      
      let semanaLabel = '-';
      if (s.fechas.length > 0) {
        const fechasOrdenadas = [...s.fechas].sort();
        const primeraKey = getISOWeekKey(fechasOrdenadas[0]);
        const ultimaKey = getISOWeekKey(fechasOrdenadas[fechasOrdenadas.length - 1]);
        const primeraSem = primeraKey.split('-')[1];
        const ultimaSem = ultimaKey.split('-')[1];
        semanaLabel = primeraSem === ultimaSem ? `S${primeraSem}` : `S${primeraSem} - S${ultimaSem}`;
      }

      return {
        piscina_id: p.id,
        piscina_codigo: p.codigo,
        sector_nombre: p.sector_nombre || '-',
        area_ha: areaHa,
        semana: semanaLabel,
        proyectado: s.proyectado,
        dosificado: s.dosificado,
        proyectadoHaKg: areaHa > 0 ? s.proyectado / areaHa : null,
        dosificadoHaKg: areaHa > 0 ? s.dosificado / areaHa : null,
        dias: s.dias.size,
        pct: s.proyectado > 0 ? (s.dosificado / s.proyectado) * 100 : null,
      };
    })
    .filter((p) => p.proyectado > 0)
    .sort((a, b) => {
      const sa = (a.sector_nombre || '').localeCompare(b.sector_nombre || '');
      if (sa !== 0) return sa;
      return (a.piscina_codigo || '').localeCompare(b.piscina_codigo || '');
    });

    const totalHectareas = resumenPiscinas.reduce((a, p) => a + (p.area_ha || 0), 0);
    const totalProyectadoHaKg = totalHectareas > 0 ? totalProyectado / totalHectareas : null;
    const totalDosificadoHaKg = totalHectareas > 0 ? totalDosificado / totalHectareas : null;
  const detalleAgrupado = (() => {
    if (!piscinaId) return [];

    if (agruparPorSemana) {
      const gruposSem = {};
      datosFiltrados.forEach((r) => {
        const key = getISOWeekKey(r.fecha);
        if (!gruposSem[key]) {
          gruposSem[key] = {
            key,
            fechas: [],
            proyectado: 0,
            dosificado: 0,
            modo: r.modo_alimentacion,
            tipo_balanceado: r.tipo_balanceado || '',
            observaciones: '',
          };
        }
        const g = gruposSem[key];
        g.fechas.push(r.fecha);
        g.proyectado += Number(r.dosis_proyectada_kg || 0);
        g.dosificado += Number(r.dosificado_pondlogs || 0);
        if (!g.modo && r.modo_alimentacion) g.modo = r.modo_alimentacion;
        if (!g.tipo_balanceado && r.tipo_balanceado) g.tipo_balanceado = r.tipo_balanceado;
        if (!g.observaciones && r.observaciones) g.observaciones = r.observaciones;
      });

      return Object.values(gruposSem)
        .sort((a, b) => a.key.localeCompare(b.key))
        .map((g) => {
          const fechasOrdenadas = [...g.fechas].sort();
          const primera = fechasOrdenadas[0];
          const ultima = fechasOrdenadas[fechasOrdenadas.length - 1];
          const [, weekStr] = g.key.split('-');
          const semanaNum = String(weekStr).padStart(2, '0');

          return {
            id: g.key,
            esSemana: true,
            fechaLabel: `S${semanaNum}`,
            semanaNum,
            rango: `${formatearFechaCorta(primera)} → ${formatearFechaCorta(ultima)}`,
            modo_alimentacion: g.modo,
            tipo_balanceado: g.tipo_balanceado,
            observaciones: g.observaciones,
            proyectadoKg: g.proyectado,
            dosificadoKg: g.dosificado,
            pct: g.proyectado > 0 ? (g.dosificado / g.proyectado) * 100 : null,
            numDias: g.fechas.length,
          };
        });
    }

    return [...datosFiltrados]
      .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''))
      .map((r) => {
        const proy = Number(r.dosis_proyectada_kg || 0);
        const dosi = Number(r.dosificado_pondlogs || 0);
        return {
          ...r,
          esSemana: false,
          proyectadoKg: proy,
          dosificadoKg: dosi,
          pct: proy > 0 ? (dosi / proy) * 100 : null,
        };
      });
  })();

  const diasPorModo = {
    cronograma: new Set(),
    sonico: new Set(),
    hibrido: new Set(),
    automatico: new Set(),
  };
  datosFiltrados.forEach((r) => {
    if (r.modo_alimentacion && diasPorModo[r.modo_alimentacion]) {
      diasPorModo[r.modo_alimentacion].add(r.fecha);
    }
  });

  const totalDiasActivos = new Set(datosFiltrados.map((d) => d.fecha)).size;

  const conteoModos = [
    { key: 'cronograma', label: 'Cronograma', dias: diasPorModo.cronograma.size, color: colorModo.cronograma },
    { key: 'sonico', label: 'Automático', dias: diasPorModo.sonico.size, color: colorModo.sonico },
    { key: 'hibrido', label: 'Híbrido', dias: diasPorModo.hibrido.size, color: colorModo.hibrido },
  ];

  const LabelPorcentaje = (props) => {
    const { x, y, width, value } = props;
    if (value == null || isNaN(value)) return null;
    return (
      <text x={x + width + 18} y={y - 12} fill="#111" textAnchor="middle" fontSize={12} fontWeight={700}>
        {`${Number(value).toFixed(1)}%`}
      </text>
    );
  };

  const LabelValorBarra = (props) => {
    const { x, y, width, height, value } = props;
    if (value == null) return null;
    const cx = x + width / 2;
    const cy = y + height / 2;
    return (
      <text
        x={cx} y={cy} fill="#fff" textAnchor="middle"
        dominantBaseline="middle" fontSize={11} fontWeight={700}
        transform={`rotate(-90, ${cx}, ${cy})`}
      >
        {Number(value).toFixed(0)}
      </text>
    );
  };

  const piscinaSeleccionada = piscinaId
    ? todasPiscinas.find((p) => p.id === piscinaId)
    : null;

  const semDesde = semanasDisponibles.find((s) => s.key === semanaDesdeKey);
  const semHasta = semanasDisponibles.find((s) => s.key === semanaHastaKey);

  const aniosDisponibles = [...new Set(semanasDisponibles.map((s) => s.year))]
    .sort((a, b) => b - a);

  const inputBase = 'w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';
  const labelBase = 'text-[10px] font-medium text-gray-500';

  const resumenFiltros = () => {
    const partes = [];
    if (modoFecha === 'rango') {
      partes.push(`Rango: ${fechaDesde} → ${fechaHasta}`);
    } else if (modoFecha === 'semana') {
      if (semDesde) partes.push(`Semana ${String(semDesde.semana).padStart(2, '0')} · ${semDesde.year}`);
    } else if (modoFecha === 'rangoSemanas' || modoFecha === 'historial') {
      if (semDesde && semHasta) {
        const prefijo = modoFecha === 'historial' ? 'Historial (cosechas): ' : '';
        partes.push(
          `${prefijo}Sem ${String(semDesde.semana).padStart(2, '0')} a ${String(semHasta.semana).padStart(2, '0')} · ${semDesde.year}`
        );
      }
    }
    if (sectorId) {
      const s = sectores.find((x) => x.id === sectorId);
      partes.push(s?.nombre || 'Sector');
    }
    if (piscinaId && piscinaSeleccionada) partes.push(piscinaSeleccionada.codigo);
    if (siembraId) partes.push('Corrida');
    if (tipoBalanceadoFiltro) partes.push(tipoBalanceadoFiltro);
    return partes.join(' · ');
  };

  return (
    <div className="p-6">

      {/* ─── Filtros ─── */}
      <div className="sticky top-0 z-40 -mx-6 px-6 pt-4 pb-3 bg-gray-100 mb-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 max-w-6xl mx-auto">

          <div className="flex justify-between items-center px-4 py-2 border-b border-gray-100">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setFiltrosExpandidos((v) => !v)}
                className="text-primary text-sm font-semibold flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-100 transition"
                title={filtrosExpandidos ? 'Ocultar filtros' : 'Mostrar filtros'}
              >
                {filtrosExpandidos ? '🔽 Ocultar Filtros' : '▶️ Mostrar Filtros'}
              </button>

              {!filtrosExpandidos && (
                <span className="text-[11px] text-gray-500 truncate max-w-2xl border-l pl-3">
                  {resumenFiltros()}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={exportarExcel}
                disabled={datos.length === 0}
                title="Exportar a Excel"
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2" fill="white" />
                  <path d="M8 8l8 8M16 8l-8 8" stroke="#16A34A" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
                <span className="text-[11px] font-medium">Excel</span>
              </button>
            </div>
          </div>

          {filtrosExpandidos && (
            <div className="px-4 py-5 flex items-center gap-6 flex-wrap justify-center">
              <div className="flex flex-col gap-1.5">
                <button type="button" onClick={() => cambiarModo('rango')} className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${modoFecha === 'rango' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>📆 Rango días</button>
                <button type="button" onClick={() => cambiarModo('semana')} className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${modoFecha === 'semana' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>📊 Semana</button>
                <button type="button" onClick={() => cambiarModo('rangoSemanas')} className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${modoFecha === 'rangoSemanas' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>📈 Rango semanas</button>
                <button type="button" onClick={() => cambiarModo('historial')} className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${modoFecha === 'historial' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>📚 Historial</button>
              </div>

              <div className="flex flex-col gap-1.5 min-w-[150px]">
                {modoFecha === 'rango' && (
                  <>
                    <label className="block"><span className={labelBase}>Desde</span><input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className={`mt-0.5 ${inputBase}`} /></label>
                    <label className="block"><span className={labelBase}>Hasta</span><input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className={`mt-0.5 ${inputBase}`} /></label>
                  </>
                )}
                {modoFecha === 'semana' && (
                  <>
                    <label className="block">
                      <span className={labelBase}>Semana</span>
                      <select value={semanaDesdeKey} onChange={(e) => cambiarSemanaUnica(e.target.value)} disabled={cargandoSemanas} className={`mt-0.5 min-w-[260px] ${inputBase}`}>
                        {cargandoSemanas ? <option value="">Cargando...</option> : semanasDisponibles.length === 0 ? <option value="">Sin datos</option> : (<> <option value="">— Seleccionar —</option> {aniosDisponibles.map((anio) => { const semanasAnio = semanasDisponibles.filter((s) => s.year === anio); return (<optgroup key={anio} label={`Año ${anio}`}> {semanasAnio.map((s) => (<option key={s.key} value={s.key}>Semana {String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)} · {s.total} reg.</option>))} </optgroup>); })} </>)}
                      </select>
                    </label>
                    {semDesde && (<div className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 whitespace-nowrap">📊 Semana {String(semDesde.semana).padStart(2, '0')} · {semDesde.year}: {fmtCorto(semDesde.lunes)} → {fmtCorto(semDesde.domingo)}</div>)}
                  </>
                )}
                {(modoFecha === 'rangoSemanas' || modoFecha === 'historial') && (
                  <>
                    <label className="block">
                      <span className={labelBase}>Semana desde</span>
                      <select value={semanaDesdeKey} onChange={(e) => cambiarSemanaDesde(e.target.value)} disabled={cargandoSemanas} className={`mt-0.5 min-w-[220px] ${inputBase}`}>
                        {cargandoSemanas ? <option value="">Cargando...</option> : semanasDisponibles.length === 0 ? <option value="">Sin datos</option> : (<> <option value="">— Desde —</option> {aniosDisponibles.map((anio) => { const semanasAnio = semanasDisponibles.filter((s) => s.year === anio); return (<optgroup key={anio} label={`Año ${anio}`}> {semanasAnio.map((s) => (<option key={s.key} value={s.key}>S{String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)}</option>))} </optgroup>); })} </>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className={labelBase}>Semana hasta</span>
                      <select value={semanaHastaKey} onChange={(e) => cambiarSemanaHasta(e.target.value)} disabled={cargandoSemanas} className={`mt-0.5 min-w-[220px] ${inputBase}`}>
                        {cargandoSemanas ? <option value="">Cargando...</option> : semanasDisponibles.length === 0 ? <option value="">Sin datos</option> : (<> <option value="">— Hasta —</option> {aniosDisponibles.map((anio) => { const semanasAnio = semanasDisponibles.filter((s) => s.year === anio); return (<optgroup key={anio} label={`Año ${anio}`}> {semanasAnio.map((s) => (<option key={s.key} value={s.key}>S{String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)}</option>))} </optgroup>); })} </>)}
                      </select>
                    </label>
                    {semDesde && semHasta && (<div className={`text-[10px] border rounded-lg px-2 py-1 whitespace-nowrap ${modoFecha === 'historial' ? 'text-purple-700 bg-purple-50 border-purple-200' : 'text-blue-700 bg-blue-50 border-blue-200'}`}>{modoFecha === 'historial' ? `📚 Historial (cosechas finales): Sem ${String(semDesde.semana).padStart(2, '0')} a Sem ${String(semHasta.semana).padStart(2, '0')} · ${semDesde.year}` : `📈 ${semanaDesdeKey === semanaHastaKey ? `Semana ${String(semDesde.semana).padStart(2, '0')} · ${semDesde.year}` : `Sem ${String(semDesde.semana).padStart(2, '0')} → Sem ${String(semHasta.semana).padStart(2, '0')} · ${semDesde.year}`}`} {' '}({fmtCorto(semDesde.lunes)} → {fmtCorto(semHasta.domingo)})</div>)}
                  </>
                )}
              </div>

              <div className="flex flex-col gap-1.5 min-w-[180px]">
                <label className="block">
                  <span className={labelBase}>Sector</span>
                  <select value={sectorId} onChange={(e) => setSectorId(e.target.value)} className={`mt-0.5 ${inputBase}`}>
                    <option value="">Todos</option>
                    {sectoresVisibles.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </label>
                <div className="relative z-50">
                  <PiscinaBuscador
                    piscinas={piscinasFiltradasParaModo(piscinas)}
                    value={piscinaId}
                    onChange={(id) => setPiscinaId(id)}
                    disabled={!sectorId}
                    permitirTodas={true}
                    valorTodas=""
                    labelTodas={modoFecha === 'historial' ? (cargandoCosechadas ? 'Cargando piscinas...' : 'Todas las cosechadas') : 'Todas las piscinas activas'}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5 min-w-[180px]">
                {modoFecha === 'historial' && (
                  <label className="block">
                    <span className={labelBase}>Corrida</span>
                    <select value={siembraId} onChange={(e) => setSiembraId(e.target.value)} disabled={!piscinaId || corridas.length === 0} className={`mt-0.5 ${inputBase} disabled:bg-gray-50 disabled:text-gray-400`}>
                      <option value="">Todas</option>
                      {corridas.map((c) => (<option key={c.id} value={c.id}>#{c.numero_corrida || '?'} - {format(new Date(c.fecha_siembra), 'dd/MM/yyyy')}</option>))}
                    </select>
                  </label>
                )}
                <label className="block">
                  <span className={labelBase}>Tipo de balanceado</span>
                  <select value={tipoBalanceadoFiltro} onChange={(e) => setTipoBalanceadoFiltro(e.target.value)} className={`mt-0.5 ${inputBase}`}>
                    <option value="">Todos</option>
                    {tiposBalanceadoUsados.map((t) => (<option key={t} value={t}>{t}</option>))}
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 flex justify-end max-w-6xl mx-auto">
        <button type="button" onClick={() => setMostrarResumen((v) => !v)} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-primary px-3 py-1.5 rounded hover:bg-gray-100">
          <span className="text-xs">{mostrarResumen ? '▼' : '▶'}</span>
          <span>{mostrarResumen ? 'Ocultar resumen' : 'Mostrar resumen'}</span>
        </button>
      </div>

  {mostrarResumen && (
  <div className="max-w-6xl mx-auto">
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
      <div className="bg-blue-50 p-4 rounded shadow">
        <div className="text-xs text-gray-600">Total proyectado</div>
        <div className="text-2xl font-bold text-blue-700">{totalProyectado.toFixed(2)} kg</div>
        <div className="text-xs text-gray-500 mt-0.5">{totalProyectadoSacos.toFixed(2)} sacos</div>
      </div>
      <div className="bg-green-50 p-4 rounded shadow">
        <div className="text-xs text-gray-600">Total dosificado</div>
        <div className="text-2xl font-bold text-green-700">{totalDosificado.toFixed(2)} kg</div>
        <div className="text-xs text-gray-500 mt-0.5">{totalDosificadoSacos.toFixed(2)} sacos</div>
      </div>
      <div className="bg-blue-50 p-4 rounded shadow border-l-4 border-blue-500">
        <div className="text-xs text-gray-600">Proyectado kg/Ha</div>
        <div className="text-2xl font-bold text-blue-700">
          {totalProyectadoHaKg != null ? totalProyectadoHaKg.toFixed(2) : '-'}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{totalHectareas.toFixed(2)} Ha totales</div>
      </div>
      <div className="bg-green-50 p-4 rounded shadow border-l-4 border-green-500">
        <div className="text-xs text-gray-600">Dosificado kg/Ha</div>
        <div className="text-2xl font-bold text-green-700">
          {totalDosificadoHaKg != null ? totalDosificadoHaKg.toFixed(2) : '-'}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{totalHectareas.toFixed(2)} Ha totales</div>
      </div>
      <div className="p-4 rounded shadow" style={{ backgroundColor: colorPorcentaje(pctTotal) + '20' }}>
        <div className="text-xs text-gray-600">Cumplimiento global</div>
        <div className="text-2xl font-bold" style={{ color: colorPorcentaje(pctTotal) }}>
          {pctTotal != null ? `${pctTotal.toFixed(1)}%` : '-'}
        </div>
        <div className="text-xs mt-1">{textoSemaforo(pctTotal)}</div>
      </div>
      <div className="bg-gray-50 p-4 rounded shadow">
        <div className="text-xs text-gray-600">Días con registro</div>
        <div className="text-2xl font-bold text-gray-700">{totalDiasActivos}</div>
      </div>
    </div>

    <div className="bg-white p-4 rounded shadow mb-4 text-center">
      <h3 className="font-semibold text-lg mb-3">Días por modo de alimentación</h3>
      <p className="text-xs text-gray-500 mb-4">
        Sobre un total de <strong>{totalDiasActivos}</strong> días con registro en el período seleccionado.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {conteoModos.map((m) => {
          const pct = totalDiasActivos > 0 ? (m.dias / totalDiasActivos) * 100 : 0;
          return (
            <div key={m.key} className="flex flex-col items-center justify-center py-3" style={{ borderTop: `4px solid ${m.color}` }}>
              <div className="text-sm font-medium text-gray-700 mb-1">{m.label}</div>
              <div className="text-2xl font-bold mb-2" style={{ color: m.color }}>{m.dias} {m.dias === 1 ? 'día' : 'días'}</div>
              <div className="w-full max-w-[200px]">
                <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: m.color }}></div>
                </div>
                <div className="text-xs text-gray-600 mt-1 text-center">{pct.toFixed(1)}% del total</div>
              </div>
            </div>
          );
        })} 
      </div>
    </div>
  </div>
  )}      {/* Cierre del mostrarResumen */}

            {/* ─── TABLA DE RESUMEN POR PISCINA ─── */}
      {resumenPiscinas.length > 0 && (
        <div className="max-w-6xl mx-auto mt-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-700">Resumen por Piscina</h3>
          </div>
          <div className="overflow-auto max-h-[600px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-100 [&_th]:px-3 [&_th]:py-3">
                <tr>
                  <th>Sector</th>
                  <th>Piscina</th>
                  <th className="text-center">Ha</th>
                  <th className="text-center">Semanas</th>
                  <th className="text-right">Proyectado (kg)</th>
                  <th className="text-right">Dosificado (kg)</th>
                  <th className="text-center">Días</th>
                  <th className="text-center">% Cumpl.</th>
                </tr>
              </thead>
              <tbody>
                {resumenPiscinas.map((p, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2">{p.sector_nombre}</td>
                    <td className="px-3 py-2 font-medium">{p.piscina_codigo}</td>
                    <td className="px-3 py-2 text-center">{p.area_ha.toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">{p.semana}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.proyectado.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.dosificado.toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">{p.dias}</td>
                    <td className="px-3 py-2 text-center font-bold" style={{ color: colorPorcentaje(p.pct) }}>
                      {p.pct != null ? `${p.pct.toFixed(1)}%` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Fila de totales */}
              <tfoot className="bg-gray-50 font-bold border-t-2 border-gray-300">
                <tr>
                  <td colSpan={2} className="px-3 py-3 text-center">TOTALES</td>
                  <td className="px-3 py-3 text-center">{totalHectareas.toFixed(2)}</td>
                  <td className="px-3 py-3 text-center">-</td>
                  <td className="px-3 py-3 text-right font-mono text-blue-700">{totalProyectado.toFixed(2)}</td>
                  <td className="px-3 py-3 text-right font-mono text-green-700">{totalDosificado.toFixed(2)}</td>
                  <td className="px-3 py-3 text-center">{totalDiasActivos}</td>
                  <td className="px-3 py-3 text-center" style={{ color: colorPorcentaje(pctTotal) }}>
                    {pctTotal != null ? `${pctTotal.toFixed(1)}%` : '-'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ─── TABLA DE DETALLE (Días o Semanas) ─── */}
      {detalleAgrupado.length > 0 && (
        <div className="max-w-6xl mx-auto mt-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-10">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-700">
              Detalle por {agruparPorSemana ? 'Semana' : 'Día'} 
              {piscinaSeleccionada ? ` — ${piscinaSeleccionada.codigo}` : ''}
            </h3>
          </div>
          <div className="overflow-auto max-h-[600px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-100 [&_th]:px-3 [&_th]:py-3">
                <tr>
                  <th>{agruparPorSemana ? 'Semana' : 'Fecha'}</th>
                  {agruparPorSemana && <th>Rango</th>}
                  <th>Modo</th>
                  <th>Tipo balanceado</th>
                  <th className="text-right">Proyectado (kg)</th>
                  <th className="text-right">Dosificado (kg)</th>
                  <th className="text-center">% Cumpl.</th>
                  <th className="text-center">Estado</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {detalleAgrupado.map((d, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">
                      {agruparPorSemana ? `S${d.semanaNum}` : formatearFechaCorta(d.fecha)}
                    </td>
                    {agruparPorSemana && <td className="px-3 py-2 text-xs text-gray-500">{d.rango}</td>}
                    <td className="px-3 py-2">
                      {d.modo_alimentacion ? (
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorModo[d.modo_alimentacion] || '#9CA3AF' }}></span>
                          {textoModo[d.modo_alimentacion] || d.modo_alimentacion}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 text-xs">{d.tipo_balanceado || '-'}</td>
                    <td className="px-3 py-2 text-right font-mono">{d.proyectadoKg.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{d.dosificadoKg.toFixed(2)}</td>
                    <td className="px-3 py-2 text-center font-bold" style={{ color: colorPorcentaje(d.pct) }}>
                      {d.pct != null ? `${d.pct.toFixed(1)}%` : '-'}
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {textoSemaforo(d.pct)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate" title={d.observaciones}>
                      {d.observaciones || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Mensaje cuando no hay datos */}
      {!cargando && datosFiltrados.length === 0 && (
        <div className="max-w-6xl mx-auto mt-8 text-center text-gray-500">
          No hay registros para los filtros seleccionados.
        </div>
      )}

    </div>
  );
}